import { DateTime } from 'luxon';
import type {
  SyncedTripLeg,
  TripLeg,
  TripLegAddress,
} from '../data/models/reservation.model';
import { TripLegStatusBC } from '../data/models/trip-leg-status.model';
import { TripLegExceptionBC } from '../data/models/trip-leg-exception.model';
import { TripLegExceptionLogEntry } from '../data/models/trip-leg.model';
import { TripLegConfidenceRequest } from '../data/requests/trip-leg-confidence';
import { TransportationAttribute } from '../data/models/transportation-attribute.model';
import { AMBULATORY_BC } from '../data/constants';

const RIDESHARE_TRIP_MODES = new Set(['AMBI']);
const RIDESHARE_CARE_LEVELS = new Set(['C2C']);

class TripLegUtils {
  public formatDistance({
    distance,
    precision = 1,
    unitPlural = 'miles',
    unitSingular = 'mile',
  }: {
    distance: number;
    precision?: number;
    unitPlural?: string;
    unitSingular?: string;
  }): string {
    if (distance === 1) {
      return `${Number.parseFloat(distance.toFixed(precision))} ${unitSingular}`;
    }
    return `${Number.parseFloat(distance.toFixed(precision))} ${unitPlural}`;
  }

  public isMemberContactExceptionBC(
    exceptionBC: string | null | undefined,
  ): boolean {
    return (
      exceptionBC === TripLegExceptionBC.NoDriver ||
      exceptionBC === TripLegExceptionBC.DriverNotOnline
    );
  }

  public getExceptionTranslationString(exceptionCode: string): string | null {
    if (!exceptionCode) return null;
    switch (exceptionCode.toUpperCase()) {
      case 'DRIVERLATE':
        return 'tripLegException.driverLate';
      case 'NODRIVER':
        return 'tripLegException.noDriver';
      case 'DRIVERNOTONLINE':
        return 'tripLegException.driverNotOnline';
      case 'OPENSAMEDAY':
        return 'tripLegException.open';
      default:
        return null;
    }
  }

  public getLatestExceptionAssignmentLog(
    tripLeg: SyncedTripLeg | TripLeg,
  ): TripLegExceptionLogEntry | null {
    if (
      tripLeg.tripLegExceptionLogs &&
      tripLeg.tripLegExceptionLogs?.length > 0
    ) {
      const sortedLogs = [...tripLeg.tripLegExceptionLogs]
        .filter(
          (log) =>
            log.assignedToUser !== null && log.assignedToUser !== undefined,
        )
        .sort(
          (a, b) =>
            DateTime.fromISO(b.createdOnUTC).toMillis() -
            DateTime.fromISO(a.createdOnUTC).toMillis(),
        );
      return sortedLogs[0];
    }
    return null;
  }

  public getExceptionAssignmentUserName(tripLeg: TripLeg): string | null {
    if (
      tripLeg.tripLegExceptionLogs &&
      tripLeg.tripLegExceptionLogs?.length > 0
    ) {
      const mostRecentAssignmentLog =
        this.getLatestExceptionAssignmentLog(tripLeg);
      if (
        mostRecentAssignmentLog?.assignedToUser?.userFirstName &&
        mostRecentAssignmentLog?.assignedToUser?.userLastName
      ) {
        return `${mostRecentAssignmentLog?.assignedToUser?.userFirstName} ${mostRecentAssignmentLog?.assignedToUser?.userLastName}`;
      }
    }
    return null;
  }

  public getExceptionAssignmentUserID(
    tripLeg: SyncedTripLeg | TripLeg,
  ): number | null {
    if (
      tripLeg.tripLegExceptionLogs &&
      tripLeg.tripLegExceptionLogs?.length > 0
    ) {
      const mostRecentAssignmentLog =
        this.getLatestExceptionAssignmentLog(tripLeg);
      if (mostRecentAssignmentLog?.assignedToUser?.userAccountID) {
        return mostRecentAssignmentLog?.assignedToUser?.userAccountID;
      }
    }
    return null;
  }

  public canReopenTripLeg(tripLeg: TripLeg): boolean {
    const tripModeBC = tripLeg.tripModeBC?.toUpperCase();
    const passengerCareLevelBC = tripLeg.passengerCareLevelBC?.toUpperCase();

    return Boolean(
      tripModeBC &&
      RIDESHARE_TRIP_MODES.has(tripModeBC) &&
      passengerCareLevelBC &&
      RIDESHARE_CARE_LEVELS.has(passengerCareLevelBC) &&
      (tripLeg.tripLegRejectedReasons || tripLeg.tripLegRejectedReasonBC) &&
      tripLeg.tripLegStatusBC === TripLegStatusBC.Open,
    );
  }

  public getDefaultTripEventDate(tripTimeZone?: string): DateTime {
    if (!tripTimeZone) {
      return DateTime.now();
    }

    const nowInTripTimeZone = DateTime.now().setZone(tripTimeZone);
    return nowInTripTimeZone.isValid ? nowInTripTimeZone : DateTime.now();
  }

  private resolveTripEventDateTime(
    eventDate: DateTime | null | undefined,
    tripTimeZone?: string,
  ): DateTime | null {
    if (!eventDate?.isValid) {
      return null;
    }

    if (!tripTimeZone) {
      return eventDate;
    }

    const tripDateTime = eventDate.setZone(tripTimeZone, {
      keepLocalTime: true,
    });

    return tripDateTime.isValid ? tripDateTime : eventDate;
  }

  public toTripEventDateTimeUTCISO(
    eventDate: DateTime | null | undefined,
    tripTimeZone?: string,
  ): string | null {
    const resolvedDateTime = this.resolveTripEventDateTime(
      eventDate,
      tripTimeZone,
    );
    return resolvedDateTime?.toUTC().toISO() ?? null;
  }

  public getTimeSinceLastLog(tripLegLogs: any[]): string {
    if (!tripLegLogs || tripLegLogs.length === 0) {
      return '';
    }
    const sortedLogs = [...tripLegLogs].sort(
      (a, b) =>
        DateTime.fromISO(a.createdOnUTC).toMillis() -
        DateTime.fromISO(b.createdOnUTC).toMillis(),
    );
    const startIndex = sortedLogs.findIndex(
      (e) =>
        e.tripLegExceptionID !== null && e.tripLegExceptionID !== undefined,
    );
    const createdOnUTC = sortedLogs[startIndex]?.createdOnUTC;
    if (!createdOnUTC) {
      return '';
    }
    const createdTime = DateTime.fromISO(createdOnUTC);
    if (!createdTime.isValid) {
      return '';
    }
    const now = DateTime.now();
    const durationMillis = Math.abs(now.toMillis() - createdTime.toMillis());
    const totalMinutes = Math.floor(durationMillis / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  }

  public getTripLegF1PayloadFromTransportResponse(
    tripLeg: SyncedTripLeg,
  ): TripLeg {
    const newTripLeg = {} as TripLeg;
    newTripLeg.passengerUUID = tripLeg.passengerUUID!;
    newTripLeg.reservationReferenceID =
      tripLeg.reservationReferenceID ?? undefined;
    newTripLeg.tripLegSequence = tripLeg.tripLegSequence;
    newTripLeg.tripLegReferenceID = tripLeg.tripLegReferenceID;
    newTripLeg.appointmentDate = tripLeg.appointmentDate;
    newTripLeg.clientBC = tripLeg.clientBC;
    newTripLeg.state = tripLeg.state;
    newTripLeg.status = tripLeg.status;
    // newTripLeg.tripLegStatusReasonBCs = [tripLeg.tripLegStatusReasonBC];
    // newTripLeg.tripLegStatusDateTimeUTC = tripLeg.tripLegStatusDateTimeUTC;
    newTripLeg.pickUpAddress = {
      addressLine1: tripLeg.pickUpAddressLine1 || '',
      addressLine2: tripLeg.pickUpAddressLine2 || '',
      city: tripLeg.pickUpCity || '',
      stateCode: tripLeg.pickUpStateCode || '',
      zipCode: tripLeg.pickUpZipCode || '',
    };
    newTripLeg.dropOffAddress = {
      addressLine1: tripLeg.dropOffAddressLine1 || '',
      addressLine2: tripLeg.dropOffAddressLine2 || '',
      city: tripLeg.dropOffCity || '',
      stateCode: tripLeg.dropOffStateCode || '',
      zipCode: tripLeg.dropOffZipCode || '',
    };
    newTripLeg.overrides = [];
    return newTripLeg;
  }

  // Matching requires the reservation trip leg: service-plan eligible services,
  // isWillCall, and transportation attributes are not on the trip search row.
  public getProvidersMatchingTripLegRequest(tripLeg: TripLeg): TripLeg {
    const address = tripLeg.pickUpAddress;
    const pickUpAddress = address?.addressLine1
      ? {
          addressLine1: address.addressLine1,
          ...(address.addressLine2
            ? { addressLine2: address.addressLine2 }
            : {}),
          ...(address.city ? { city: address.city } : {}),
          stateCode: address.stateCode,
          ...(address.county ? { county: address.county } : {}),
          ...(address.zipCode ? { zipCode: address.zipCode } : {}),
          ...(address.timeZone ? { timeZone: address.timeZone } : {}),
          ...(typeof address.latitude === 'number'
            ? { latitude: address.latitude }
            : {}),
          ...(typeof address.longitude === 'number'
            ? { longitude: address.longitude }
            : {}),
        }
      : undefined;

    return {
      clientBC: tripLeg.clientBC,
      appointmentDate: tripLeg.appointmentDate,
      isWillCall: !!tripLeg.isWillCall,
      ...(tripLeg.pickUpDueDateTime
        ? { pickUpDueDateTime: tripLeg.pickUpDueDateTime }
        : {}),
      ...(typeof tripLeg.estimatedDistance === 'number'
        ? { estimatedDistance: tripLeg.estimatedDistance }
        : {}),
      pickUpAddress,
      transportationAttributes: tripLeg.transportationAttributes ?? [],
      servicePlans: (tripLeg.servicePlans ?? []).map((plan) => ({
        servicePlanUUID: plan.servicePlanUUID,
        rejectedReasons: Array.isArray(plan.rejectedReasons)
          ? plan.rejectedReasons
          : [],
        eligibleTransportationServiceUUIDs: Array.isArray(
          plan.eligibleTransportationServiceUUIDs,
        )
          ? plan.eligibleTransportationServiceUUIDs
          : [],
      })),
    } as TripLeg;
  }

  public getTripLegConfidencePayload(
    tripLeg: Partial<TripLeg>,
  ): TripLegConfidenceRequest {
    const confidencePayload = {} as TripLegConfidenceRequest;
    confidencePayload.pickUpAddressLine1 =
      tripLeg.pickUpAddress?.addressLine1 || '';
    confidencePayload.pickUpAddressLine2 =
      tripLeg.pickUpAddress?.addressLine2 || '';
    confidencePayload.pickUpCity = tripLeg.pickUpAddress?.city || '';
    confidencePayload.pickUpStateCode = tripLeg.pickUpAddress?.stateCode || '';
    confidencePayload.pickUpZipCode = tripLeg.pickUpAddress?.zipCode || '';
    confidencePayload.dropOffAddressLine1 =
      tripLeg.dropOffAddress?.addressLine1 || '';
    confidencePayload.dropOffAddressLine2 =
      tripLeg.dropOffAddress?.addressLine2 || '';
    confidencePayload.dropOffCity = tripLeg.dropOffAddress?.city || '';
    confidencePayload.dropOffStateCode =
      tripLeg.dropOffAddress?.stateCode || '';
    confidencePayload.dropOffZipCode = tripLeg.dropOffAddress?.zipCode || '';
    return confidencePayload;
  }

  /**
   * Single-line address built from an AddressInput option, used as the
   * /calculate-pickup-time fallback when the leg has no `*AddressStruct`
   * (Place Details failed, or a saved passenger address without a place id).
   * Uses the option's main text, secondary text and zip rather than its
   * description, which for saved addresses is "<label> - <line1>, <city>"
   * with no state or zip.
   */
  public getAddressOptionLine(option: unknown): string {
    if (option == null) return '';
    if (typeof option === 'string') return option;
    if (typeof option !== 'object') return '';
    const p = option as {
      isHeader?: boolean;
      description?: string;
      mainText?: string;
      secondaryText?: string;
      zipCode?: string;
    };
    if (p.isHeader) return p.description ?? '';
    const zip = p.zipCode?.trim() ?? '';
    if (p.mainText) {
      let cityState = p.secondaryText ?? '';
      // Saved-address secondary text already ends in the zip; don't repeat it.
      if (zip) cityState = cityState.replace(` ${zip}`, '');
      return [p.mainText.trim(), `${cityState.trim()} ${zip}`.trim()]
        .filter(Boolean)
        .join(', ');
    }
    return `${p.description ?? ''} ${zip}`.trim();
  }

  /**
   * Single-line address for /calculate-pickup-time, which only accepts strings.
   * Built from the same `*AddressStruct` fields getTripLegConfidencePayload
   * sends to /score/trip-leg, so both calls describe the same address. Falls
   * back to `fallback` (see getAddressOptionLine) when the struct is
   * empty, unparseable, or has no street line, e.g. when Place Details failed.
   */
  public getPickupTimeAddressLine(
    addressStruct: string | null | undefined,
    fallback: string,
  ): string {
    let address: Partial<TripLegAddress> | null = null;
    try {
      address = addressStruct ? JSON.parse(addressStruct) : null;
    } catch {
      address = null;
    }
    const line1 = address?.addressLine1?.trim();
    if (!address || !line1) return fallback;
    const stateZip = [address.stateCode, address.zipCode]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' ');
    return [line1, address.addressLine2, address.city, stateZip]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(', ');
  }

  /**
   * Returns the attributes that describe a trip leg's mode, for example
   * "Non-Ambulatory" followed by "Wheelchair".
   *
   * Starts at the leg's ambulatory attribute and follows each attribute's
   * child category to the leg's attribute in that category, stopping at the
   * attribute that sets the trip mode. The API may return the leg's attributes
   * as one nested path or as separate single-item paths, so the paths are
   * flattened first and the order inside them is not relied on.
   */
  public getTripModeAttributes(
    legAttributePaths: string[][] | undefined,
    transportationAttributes: TransportationAttribute[],
  ): TransportationAttribute[] {
    const byUUID = new Map(
      transportationAttributes.map((a) => [a.transportationAttributeUUID, a]),
    );
    const legAttributes = (legAttributePaths ?? [])
      .flat()
      .map((uuid) => byUUID.get(uuid))
      .filter((a): a is TransportationAttribute => !!a);

    let current = legAttributes.find(
      (a) => a.transportationAttributeCategoryBC === AMBULATORY_BC,
    );
    const result: TransportationAttribute[] = [];
    while (current && !result.includes(current)) {
      result.push(current);
      if (
        current.tripModeBC ||
        !current.childTransportationAttributeCategoryBC
      ) {
        break;
      }
      const childCategory = current.childTransportationAttributeCategoryBC;
      current = legAttributes.find(
        (a) => a.transportationAttributeCategoryBC === childCategory,
      );
    }
    return result;
  }

  public upsertTripLegData(tripLeg: TripLeg, newTripLegData: TripLeg): TripLeg {
    const newTripLeg = { ...tripLeg, ...newTripLegData } as TripLeg;
    if (newTripLegData.dropOffAddress) {
      newTripLeg.tripLeg!.dropOffAddressLine1 =
        newTripLegData.dropOffAddress.addressLine1 || '';
      newTripLeg.tripLeg!.dropOffAddressLine2 =
        newTripLegData.dropOffAddress.addressLine2 || '';
      newTripLeg.tripLeg!.dropOffCity =
        newTripLegData.dropOffAddress.city || '';
      newTripLeg.tripLeg!.dropOffStateCode =
        newTripLegData.dropOffAddress.stateCode || '';
      newTripLeg.tripLeg!.dropOffZipCode =
        newTripLegData.dropOffAddress.zipCode || '';
    }
    if (newTripLegData.pickUpAddress) {
      newTripLeg.tripLeg!.pickUpAddressLine1 =
        newTripLegData.pickUpAddress.addressLine1 || '';
      newTripLeg.tripLeg!.pickUpAddressLine2 =
        newTripLegData.pickUpAddress.addressLine2 || '';
      newTripLeg.tripLeg!.pickUpCity = newTripLegData.pickUpAddress.city || '';
      newTripLeg.tripLeg!.pickUpStateCode =
        newTripLegData.pickUpAddress.stateCode || '';
      newTripLeg.tripLeg!.pickUpZipCode =
        newTripLegData.pickUpAddress.zipCode || '';
    }
    if (newTripLegData.appointmentDate) {
      newTripLeg.appointmentDate = newTripLegData.appointmentDate;
    }
    if (newTripLegData.appointmentDateTime) {
      newTripLeg.appointmentDateTime = newTripLegData.appointmentDateTime;
    }
    if (newTripLegData.pickUpDueDateTime) {
      newTripLeg.pickUpDueTimeUTC = newTripLegData.pickUpDueDateTime;
      if (newTripLeg.tripLeg) {
        newTripLeg.tripLeg.pickUpDueTimeUTC = newTripLegData.pickUpDueDateTime;
      }
    }
    if (newTripLegData.pickUpDueTimeUTC) {
      newTripLeg.pickUpDueTimeUTC = newTripLegData.pickUpDueTimeUTC;
    }
    newTripLeg.passengerCareLevelBC =
      newTripLegData.passengerCareLevelBC || undefined;
    newTripLeg.passengerCareLevelName =
      newTripLegData.passengerCareLevelName || undefined;
    newTripLeg.transportationProviders =
      newTripLegData.transportationProviders || [];
    if (newTripLegData.transportationProvider) {
      newTripLeg.transportationProvider = newTripLegData.transportationProvider;
    }
    if (newTripLegData.transportationAttributes) {
      newTripLeg.transportationAttributes = [
        ...newTripLegData.transportationAttributes,
      ];
    }
    if (newTripLegData.tripModeBC) {
      newTripLeg.tripModeBC = newTripLegData.tripModeBC;
      if (newTripLegData.tripModeName) {
        newTripLeg.tripModeName = newTripLeg.tripModeBC;
      } else {
        newTripLeg.tripModeName = undefined;
      }
    } else {
      newTripLeg.tripModeBC = undefined;
      newTripLeg.tripModeName = undefined;
    }
    if (newTripLegData.status) {
      newTripLeg.tripLegStatusBC = newTripLegData.status.toLocaleUpperCase();
      newTripLeg.tripLegStatusName =
        newTripLegData.status[0].toLocaleUpperCase() +
        newTripLegData.status.substring(1).toLocaleLowerCase();
    }
    return newTripLeg;
  }
}

const tripLegUtils = new TripLegUtils();

export default tripLegUtils;
